import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Stack, Typography } from '@mui/material';
import React from 'react';
import { useForm } from 'react-hook-form';
import FormProvider from 'common/components/form-provider';
import BaseTextField from 'common/components/base-text-field';
import { colors, styleInput } from 'common/constants';
import avatarDefault from 'assets/images/icons/avatar-default.svg';
import { createStyleSheet, handleError } from 'utils/helpers';
import RequiredLabel from 'common/components/required-label';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BaseImageUpload from 'common/components/base-img-upload';
import { common } from '@mui/material/colors';
import { toast } from 'react-toastify';
import { ReactComponent as SyncIcon } from 'assets/images/icons/sync.svg';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import BaseSwitch from 'common/components/base-switch';
import { customerSchema } from 'common/schema';
import {
    getClientInfo,
    updateClient,
    createClient,
} from 'services/clientManagement.service';
import { setIsLoading } from 'slices/loadingSlice';

interface ICustomerFormProps {
    edit: boolean;
}
const CustomerForm: React.FC<ICustomerFormProps> = ({ edit }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const methods = useForm({
        resolver: yupResolver(customerSchema),
        defaultValues: {
            avatar: '',
            username: '',
            email: '',
            uid: '',
            status: true,
        },
    });
    const {
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = methods;
    const dispatch = useDispatch();
    const accountId = useParams<{ id: string }>().id;
    const onSubmit = handleSubmit(async (data) => {
        try {
            if (!edit) {
                dispatch(setIsLoading(true));
                const response = await createClient(data);
                if (response.success) {
                    toast.success(response.message);
                    navigate('/admin/customer');
                    dispatch(setIsLoading(false));
                } else {
                    throw new Error(response.message);
                }
            } else {
                if (!accountId) return;
                const response = await updateClient(Number(accountId), data);
                if (response.success) {
                    toast.success(response.message);
                    navigate('/admin/customer');
                    dispatch(setIsLoading(false));
                } else {
                    throw new Error(response.message);
                }
            }
        } catch (error) {
            dispatch(setIsLoading(false));
            handleError(error);
        }
    });

    React.useEffect(() => {
        const getUserDetail = async () => {
            try {
                const response = await getClientInfo(Number(accountId));
                if (response.success) {
                    reset({
                        avatar: response.data.client.attributes.avatar,
                        username: response.data.client.attributes.username,
                        email: response.data.client.attributes.email,
                        uid: response.data.client.attributes.uid,
                        status:
                            response.data.client.attributes.status === 'active'
                                ? true
                                : false,
                    });
                }
            } catch (error) {
                handleError(error);
            }
        };
        if (edit && accountId) {
            getUserDetail();
        }
    }, [reset, edit, accountId]);
    return (
        <Box
            sx={{
                marginTop: '2rem',
            }}
        >
            <Typography variant="h4" sx={styles.title}>
                {!edit ? 'Thêm khách hàng mới' : 'Chỉnh sửa khách hàng'}
            </Typography>
            <FormProvider methods={methods} onSubmit={onSubmit}>
                <Box sx={{ marginBottom: '24px', fontWeight: '600' }}>
                    {t('common.avatar')}
                </Box>
                <Box display={'flex'} gap={'60px'}>
                    <Box>
                        <BaseImageUpload
                            name="avatar"
                            multiple={false}
                            sx={{
                                ...styles.avatar,
                                backgroundImage: watch().avatar
                                    ? `url(${watch().avatar})`
                                    : `url(${avatarDefault})`,
                                border: watch().avatar
                                    ? 'none'
                                    : `2px dotted ${colors.main}`,
                                borderRadius: '50%',
                            }}
                        />

                        {errors.avatar && (
                            <Box
                                sx={{
                                    fontSize: '14px',
                                    marginLeft: 0,
                                    color: `${colors.textError} !important`,
                                    fontWeight: '400',
                                }}
                            >
                                {errors.avatar.message}
                            </Box>
                        )}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '37px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                gap: '37px',
                            }}
                        >
                            <Stack spacing={'7px'}>
                                <RequiredLabel
                                    label={t('common.user.id')}
                                    required={false}
                                />
                                <BaseTextField
                                    autoComplete="off"
                                    name="uid"
                                    fullWidth
                                    size="small"
                                    sx={styles.input}
                                    disabled
                                />
                            </Stack>
                            <Stack spacing={'7px'}>
                                <RequiredLabel
                                    label={t('common.user.name')}
                                    required={false}
                                />
                                <BaseTextField
                                    autoComplete="off"
                                    name="username"
                                    fullWidth
                                    size="small"
                                    sx={styles.input}
                                    inputProps={{ maxLength: 55 }}
                                />
                            </Stack>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                gap: '37px',
                            }}
                        >
                            <Stack spacing={'7px'}>
                                <RequiredLabel label="Email" required={false} />
                                <BaseTextField
                                    autoComplete="off"
                                    name="email"
                                    fullWidth
                                    size="small"
                                    sx={styles.input}
                                    {...(edit && { disabled: true })}
                                />
                            </Stack>
                            <Stack spacing={'7px'} flex={1}>
                                <RequiredLabel
                                    label="Trạng thái"
                                    required={false}
                                />
                                <BaseSwitch
                                    name="status"
                                    sx={{
                                        transform: 'scale(1.5)',
                                        '& .MuiSwitch-thumb': {
                                            backgroundColor:
                                                'var(--color-primary-light)',
                                        },
                                        '& .MuiSwitch-track': {
                                            backgroundColor: '#0000001A',
                                            opacity: 1,
                                        },
                                        '& .Mui-checked': {
                                            '& .MuiSwitch-thumb': {
                                                backgroundColor:
                                                    'var(--color-primary-light)',
                                            },
                                            '& + .MuiSwitch-track': {
                                                backgroundColor: 'primary.main',
                                                opacity: '1 !important',
                                            },
                                        },
                                    }}
                                />
                            </Stack>
                        </Box>
                        <Box>
                            <Stack
                                gap={2}
                                flexDirection={'row'}
                                alignItems={'center'}
                            >
                                {!edit ? (
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        sx={styles.btnEdit}
                                    >
                                        Lưu
                                    </Button>
                                ) : (
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        sx={styles.btnEdit}
                                        startIcon={<SyncIcon />}
                                    >
                                        {t('common.update')}
                                    </Button>
                                )}
                                <Button
                                    type="submit"
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => {
                                        navigate('/admin/customer');
                                    }}
                                    startIcon={<CloseIcon />}
                                    sx={{
                                        ...styles.btnEdit,
                                        width: '155px',
                                        backgroundColor: common.white,
                                        border: `2px solid ${colors.main}`,
                                        ':hover': {
                                            backgroundColor: common.white,
                                            border: `2px solid ${colors.main}`,
                                        },
                                    }}
                                >
                                    Đóng
                                </Button>
                            </Stack>
                        </Box>
                    </Box>
                </Box>
            </FormProvider>
        </Box>
    );
};

const styles = createStyleSheet({
    input: {
        '& .MuiInputBase-root': {
            ...styleInput['& .MuiInputBase-root'],
            width: '400px',
            height: '60px',
            fontSize: '22px',
        },
    },
    title: {
        fontSize: '40px',
        fontWeight: 700,
        color: colors.main,
        marginBottom: '27px',
    },
    avatar: {
        width: '348px',
        height: '348px',
        backgroundImage: `url(${avatarDefault})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'bottom',
        borderRadius: '17px',
        border: `2px dotted ${colors.main}`,
    },
    btnEdit: {
        minWidth: '155px',
        height: '60px',
        fontSize: '20px',
        lineHeight: '24px',
        fontWeight: '700',
        borderRadius: '40px',
        textTransform: 'none',
    },
});

export default CustomerForm;
